import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { FaEllipsisH, FaMagic, FaStop, FaChevronLeft, FaChevronRight, FaPalette, FaPaperclip, FaImage, FaPaperPlane, FaChevronDown, FaUser, FaPersonBooth, FaUserAlt, FaUserSecret, FaUserEdit, FaWifi, FaSignal, FaCalendarAlt, FaTshirt, FaSpinner } from "react-icons/fa";
import { FaQuestionCircle } from "react-icons/fa";
import toast from "react-hot-toast";

import { CREATION_OPTIONS, CreationOptionsModal } from "./pills/CreationOptionsGrid.js";
import ReferenceImageModal from "./pills/ReferenceImageModal";
// import TagProductModal from "./pills/TagProductModal";
import TagProductModal1 from "./pills/TagProductModal1";
import AspectRatioModal from "./pills/AspectRatioModal";
import OutputSamplesModal from "./pills/OutputSamplesModal";
import StyleModal from "./pills/StyleModal";
import TemplateModal from "./pills/TemplateModal";
import ModelModal from "./pills/ModelModal";
import HumanModelModal from "./pills/HumanModelModal.jsx";
import EventSelectorModal from "./pills/EventSelectorModal.jsx";
import ImageModelModal from "./pills/ImageModelModal.jsx";
import ImageQualityModal from "./pills/ImageQualityModal.jsx";
import AIModelSelector from "./pills/AIModelSelector.jsx";
import { calculateImageCost, CostHelpModal } from "../utils/costCalculator";
import { loadModelSchema, calculateModelCost } from "../utils/modelSchemaLoader";

// Constants
const common_placeholder_text = 'Add any additional instructions, if any...'
// CREATION_OPTIONS is now imported from CreationOptionsGrid

// Creation option defaults
const CREATION_DEFAULTS = {
  showReferenceImage: true,
  allowedModels: null, // null => allow all models (subject to other filters)
  allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
  showOutputSamples: true,
  showImageResolution: true,
  overrideInstagramPostType: 'story',
};

// Model visibility mapping based on reference image upload status
const MODEL_VISIBILITY_MAPPING = {
  // Models that should show when NO reference image is uploaded (text-to-image models)
  noReferenceImage: [
    'bytedance/seedream-3', //txt2img
    'black-forest-labs/flux-schnell', //txt2img
    'black-forest-labs/flux-redux-schnell', //txt2img, img2img
    'openai/gpt-image-1', //txt2img, img2img
    'black-forest-labs/flux-kontext', //txt2img, img2img
    'black-forest-labs/flux-1.1-pro-ultra', //txt2img, img2img
    'black-forest-labs/flux-krea-dev', //txt2img, img2img
    'ideogram-ai/ideogram-v3', //txt2img, img2img
    'minimax/hailuo-02', //txt2vid, img2vid
    'bytedance/seedance-1-pro', //txt2vid, img2vid
    'wan-video/wan-2.2-i2v-a14b', //txt2vid, img2vid
    'google/veo-3-fast', //txt2vid, img2vid,
  ],
  // Models that should show when reference image IS uploaded (image-to-image models)
  withReferenceImage: [
    'black-forest-labs/flux-redux-schnell', //txt2img, img2img
    '851-labs/background-remover', //img2img
    'logerzhu/ad-inpaint', //img2img
    'openai/gpt-image-1', //txt2img, img2img
    'black-forest-labs/flux-kontext', //txt2img, img2img
    'black-forest-labs/flux-1.1-pro-ultra', //txt2img, img2img
    'black-forest-labs/flux-fill-pro', //img2img
    'ideogram-ai/ideogram-character', //img2img
    'black-forest-labs/flux-krea-dev', //txt2img, img2img
    'ideogram-ai/ideogram-v3', //txt2img, img2img
    'minimax/hailuo-02-fast', //img2vid
    'minimax/hailuo-02', //txt2vid, img2vid
    'bytedance/seedance-1-pro', //txt2vid, img2vid
    'wan-video/wan-2.2-i2v-fast', //img2vid
    'wan-video/wan-2.2-i2v-a14b', //txt2vid, img2vid
    'kwaivgi/kling-v2.1', //img2vid
    'google/veo-3-fast', //txt2vid, img2vid
  ]
};

const MODEL_SHOWS_OUTPUT_SAMPLES = [
  'black-forest-labs/flux-schnell',
  'black-forest-labs/flux-redux-schnell',
  'openai/gpt-image-1',
  'black-forest-labs/flux-kontext',
  'black-forest-labs/flux-1.1-pro-ultra',
  'ideogram-ai/ideogram-character',
  'black-forest-labs/flux-krea-dev',
  'ideogram-ai/ideogram-v3',
];

const MODEL_OPTIONS = [
  // Added image models
  { type:'img', id: 'black-forest-labs/flux-schnell', name: 'FLUX Schnell', desc: 'Fast image generation', meta: { outputs: 4, latencySeconds: 15, cost: 'Free' }, tags: ['Styles', 'Custom Ratios'] },
  { type:'img', id: 'black-forest-labs/flux-kontext', name: 'FLUX Kontext', desc: 'Best in class', meta: { outputs: 1, latencySeconds: 15, cost: '$0.03 to $0.08' }, tags: ['Styles', 'Custom Ratios'] },
  { type:'img', id: 'bytedance/seedream-3', name: 'Bytedance Seedream 3', cost: '', desc: 'High-quality image model', meta: { outputs: 2, latencySeconds: 1, cost: '$0.09' }, tags: ['High Quality'] },
  { type:'img', id: 'black-forest-labs/flux-redux-schnell', name: 'FLUX Redux Schnell', cost: 'Free', desc: 'Variation/pose updates', meta: { outputs: 14, latencySeconds: 8, cost: 'Free' }, tags: ['Styles'] },
  { type:'img', id: '851-labs/background-remover', name: 'Background Remover', cost: '', desc: 'Remove Background', meta: { outputs: 1, latencySeconds: 14, cost: '' }, tags: ['Edit'] },
  { type:'img', id: 'logerzhu/ad-inpaint', name: 'Product Background', cost: '', desc: 'Change Product Background', meta: { outputs: 1, latencySeconds: 12, cost: '' }, tags: ['Inpaint'] },
  { type:'img', id: 'openai/gpt-image-1', name: 'OpenAI GPT Image 1', cost: '', desc: 'Combines multiple images', meta: { outputs: 1, latencySeconds: 60, cost: '$0.03 to $0.25' }, tags: ['Presets'] },
  { type:'img', id: 'black-forest-labs/flux-1.1-pro-ultra', name: 'FLUX1.1 Ultra', cost: '$0.06/img', desc: 'UGC images', meta: { outputs: 4, latencySeconds: 18, cost: '$0.06' }, tags: ['Photoreal'] },
  { type:'img', id: 'black-forest-labs/flux-pro-1.0-fill', name: 'flux-pro-1.0-fill', cost: '', desc: 'Inpainting with mask', meta: { outputs: 1, latencySeconds: 10, cost: '' }, tags: ['Inpaint'] },
  { type:'img', id: 'ideogram-ai/ideogram-character-quality', name: 'Ideogram Create characters', cost: '$0.10 to $0.20', desc: 'Create characters in new poses from one ref', meta: { outputs: 2, latencySeconds: 12, cost: '$0.10 to $0.20' }, tags: ['Character'] },
  { type:'img', id: 'black-forest-labs/flux-krea-dev', name: 'FLUX Krea', cost: '$0.025/img', desc: 'Real-life ', meta: { outputs: 4, latencySeconds: 16, cost: '$0.025' }, tags: ['Realism'] },
  { type:'img', id: 'ideogram-ai/ideogram-v3-quality', name: 'Ideogram V3', cost: '$0.03 to $0.09/img', desc: 'Best Image Generation quality (~12s)', meta: { outputs: 2, latencySeconds: 12, cost: '$0.03 to $0.09' }, tags: ['High Quality'] },
  
  // Added video models
  // { type:'video', id: 'wan-video/wan-2.2-i2v-fast', name: 'wan-2.2-i2v-fast', cost: '$0.05/5s@480p, $0.11/5s@720p', desc: 'Image-to-Video', meta: { outputs: 1, latencySeconds: 10, computeUnits: 20 }, tags: ['i2v'] },
  // { type:'video', id: 'minimax/hailuo-02-fast', name: 'hailuo-02-fast', cost: '$0.10/6s@512p, $0.15/10s@512p', desc: 'Fast generation', meta: { outputs: 1, latencySeconds: 8, computeUnits: 18 }, tags: ['Fast'] },
  // { type:'video', id: 'minimax/hailuo-02', name: 'hailuo-02', cost: '$0.10/6s@512p, $0.27/6s@768p, $0.48/6s@1080p', desc: '', meta: { outputs: 1, latencySeconds: 12, computeUnits: 30 }, tags: ['txt2vid','i2v'] },
  // { type:'video', id: 'bytedance/seedance-1-pro', name: 'seedance-1-pro', cost: '$0.15/5s@480p, $0.30/5s@720p, $0.75/5s@1080p', desc: '', meta: { outputs: 1, latencySeconds: 9, computeUnits: 25 }, tags: ['txt2vid','i2v'] },
  // { type:'video', id: 'kwaivgi/kling-v2.1', name: 'kling-v2.1', cost: 'Std $0.25/5s@720p, Pro $0.45/5s@1080p', desc: 'Slow but cheap', meta: { outputs: 1, latencySeconds: 14, computeUnits: 28 }, tags: ['i2v'] },
  // { type:'video', id: 'wan-video/wan-2.2-i2v-a14b', name: 'wan-2.2-i2v-a14b', cost: '$0.40/5s@480p, $1/5s@720p', desc: '', meta: { outputs: 1, latencySeconds: 15, computeUnits: 30 }, tags: ['txt2vid','i2v'] },
  // { type:'video', id: 'google/veo-3-fast', name: 'veo-3-fast', cost: '$3.20/8s@720p', desc: '', meta: { outputs: 1, latencySeconds: 60, computeUnits: 200 }, tags: ['txt2vid'] },
];

// Aspect ratio icon component
// Calculates rectangle dimensions based on aspect ratio string (e.g., "16:9", "1:1")
const AspectIcon = ({ ratio, className = "w-3.5 h-3.5" }) => {
  // Helper function to parse aspect ratio and calculate rectangle dimensions
  const getRectDimensions = (ratioStr) => {
    // Parse aspect ratio string (e.g., "16:9", "1:1", "4:3")
    const parts = ratioStr.split(':');
    if (parts.length !== 2) return { x: 6, y: 6, w: 12, h: 12 }; // Default to square
    
    const widthRatio = parseFloat(parts[0]);
    const heightRatio = parseFloat(parts[1]);
    const aspectRatio = widthRatio / heightRatio;
    
    // Calculate rectangle dimensions within 24x24 viewBox with padding
    const maxSize = 16; // Maximum size with padding
    
    let rectWidth, rectHeight;
    if (aspectRatio >= 1) {
      // Landscape or square: width is the limiting factor
      rectWidth = maxSize;
      rectHeight = maxSize / aspectRatio;
    } else {
      // Portrait: height is the limiting factor
      rectHeight = maxSize;
      rectWidth = maxSize * aspectRatio;
    }
    
    // Center the rectangle
    const x = (24 - rectWidth) / 2;
    const y = (24 - rectHeight) / 2;
    
    return { x, y, w: rectWidth, h: rectHeight };
  };
  
  const dims = getRectDimensions(ratio);
  
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x={dims.x} y={dims.y} width={dims.w} height={dims.h} rx="2" ry="2" />
    </svg>
  );
};

const CreateImageEditor = ({
  // State values
  instagramPostType,
  setInstagramPostType,
  creationInputMode,
  setCreationInputMode,
  selectedCreationOption,
  setSelectedCreationOption,
  message,
  setMessage,
  messageError,
  textboxUploadedImages,
  setTextboxUploadedImages,
  textboxUploadError,
  isReferenceLoading,
  selectedModel,
  setSelectedModel,
  isModelCollapsed,
  setIsModelCollapsed,
  isOutputCollapsed,
  setIsOutputCollapsed,
  aspectRatio,
  setAspectRatio,
  reelDuration,
  setReelDuration,
  reelDurationError,
  resolution,
  setResolution,
  imageResolution,
  setImageResolution,
  outputSamples,
  setOutputSamples,
  isAudioCollapsed,
  setIsAudioCollapsed,
  audioOption,
  setAudioOption,
  audioText,
  setAudioText,
  isGeneratingContent,
  contentGenerated,
  generateContentLabel,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  // Image mode props (for text_to_image)
  imageMode,
  setImageMode,
  // Workflow completion state
  isWorkflowComplete,
  
  // Event handlers
  handleTextboxFileUpload,
  removeTextboxImage,
  handleStopGeneration,
  handleGenerateContent,
  
  // Refs
  messageTextareaRef,
  textboxFileInputRef,
  // New dresses props
  selectedDresses,
  setSelectedDresses
  ,
  // New human model props
  selectedHumanModel,
  setSelectedHumanModel,
// Event props
  selectedEvent,
  setSelectedEvent,
  // Product props
  productType,
  setProductType,
  productItem,
  setProductItem,
  // Style props
  selectedStyle,
  setSelectedStyle,
  // Template props
  selectedTemplate,
  setSelectedTemplate,
  // Real looks toggle
  realLooks,
  setRealLooks,
  setShowTour,
  // Image Quality props
  imageQuality,
  setImageQuality,
  // AI Model props
  selectedAIModel,
  setSelectedAIModel
}) => {
  // Local state for dropdowns
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  
  // New chat UI state
  const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
  const chatInputRef = useRef(null);

  // AI Model cost calculation state
  const [currentModelSchema, setCurrentModelSchema] = useState(null);

  // Keep chat input in sync with persisted message (session)
  useEffect(() => {
    setChatInput(message || '');
  }, [message]);

  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  
  // Refs for dropdowns
  const modelDropdownRef = useRef(null);
  // Removed creation dropdown; using chip selector instead
  
  // Pills modal state
  const [isRefModalOpen, setIsRefModalOpen] = useState(false);
  // const [isTagProductModalOpen, setIsTagProductModalOpen] = useState(false);
  const [isTagProductModal1Open, setIsTagProductModal1Open] = useState(false);
  const [isAspectModalOpen, setIsAspectModalOpen] = useState(false);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [isSamplesModalOpen, setIsSamplesModalOpen] = useState(false);
  const [isResModalOpen, setIsResModalOpen] = useState(false);
  const [isDurModalOpen, setIsDurModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [isImageModeModalOpen, setIsImageModeModalOpen] = useState(false);
  const [isImageQualityModalOpen, setIsImageQualityModalOpen] = useState(false);
  const [isModelHoverOpen, setIsModelHoverOpen] = useState(false);
  const modelHoverTimerRef = useRef(null);
  const [modelPillLeft, setModelPillLeft] = useState(0);
  const sidebarResizeObserverRef = useRef(null);
  const [isDressModalOpen, setIsDressModalOpen] = useState(false);
const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isHumanModelModalOpen, setIsHumanModelModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCostHelpOpen, setIsCostHelpOpen] = useState(false);
  // const [productType, setProductType] = useState('Clothing');
  // const [productItem, setProductItem] = useState('');

  useEffect(() => {
    const computeLeft = () => {
      try {
        const candidates = [];
        const nav = document.querySelector('nav.fixed');
        if (nav) {
          const rect = nav.getBoundingClientRect();
          candidates.push(rect.right);
        }
        const asides = Array.from(document.querySelectorAll('aside.fixed'));
        asides.forEach(a => {
          const rect = a.getBoundingClientRect();
          candidates.push(rect.right);
        });
        const maxRight = candidates.length ? Math.max(...candidates) : 0;
        const margin = 16; // px gap from sidebar
        setModelPillLeft(Math.max(12, Math.round(maxRight + margin)));
      } catch (_e) {
        setModelPillLeft(112); // safe fallback
      }
    };

    computeLeft();

    const onResize = () => computeLeft();
    window.addEventListener('resize', onResize);

    // Observe size changes of sidebars
    const ro = new ResizeObserver(() => computeLeft());
    sidebarResizeObserverRef.current = ro;
    const nav = document.querySelector('nav.fixed');
    if (nav) ro.observe(nav);
    document.querySelectorAll('aside.fixed').forEach(a => ro.observe(a));

    // Also observe class/style changes that might affect width
    const mo = new MutationObserver(() => computeLeft());
    mo.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });

    return () => {
      window.removeEventListener('resize', onResize);
      if (sidebarResizeObserverRef.current) sidebarResizeObserverRef.current.disconnect();
      mo.disconnect();
    };
  }, [isSidebarCollapsed]);

  useEffect(() => {
    return () => {
      if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
    };
  }, []);

  // Computed values
  const isFreeModel = selectedModel === 'free';
  const currentMediaType = instagramPostType === 'reel' ? 'video' : 'img';
  const placeholderText = currentMediaType === 'video'
    ? "e.g., A cinematic shot of a car driving down a rainy street at night"
    : "e.g., Create an image of a cozy cafe with warm lighting";
  const filteredModelOptions = MODEL_OPTIONS.filter(m => m.type === currentMediaType);
  const creationOptionError = !selectedCreationOption ? 'Please select a creation option.' : '';
  const resolvedCreationSelection = CREATION_OPTIONS.find(o => o.label === selectedCreationOption) || CREATION_OPTIONS.find(o => o.id === selectedCreationOption);
  const effectiveSelectedLabel = resolvedCreationSelection?.label || CREATION_OPTIONS[0]?.label;
  const primaryCreationChips = React.useMemo(() => {
    const selectedCreation = CREATION_OPTIONS.find(o => o.label === effectiveSelectedLabel);
    const base = CREATION_OPTIONS.slice(0, 1);
    if (selectedCreation && !base.some(o => o.id === selectedCreation.id)) {
      const others = CREATION_OPTIONS.filter(o => o.id !== selectedCreation.id);
      return [selectedCreation, ...others.slice(0, 0)];
    }
    return base;
  }, [effectiveSelectedLabel]);

  // Resolve creation option config based on currently selected label (parent stores label)
  const selectedCreationOptionDef = resolvedCreationSelection || CREATION_OPTIONS[0];
  const creationConfig = selectedCreationOptionDef || CREATION_DEFAULTS;
  const allowedAspectRatios = creationConfig.allowedAspectRatios || CREATION_DEFAULTS.allowedAspectRatios;
  const allowedAspectRatiosSet = new Set(allowedAspectRatios);

  // Filter models based on reference image upload status and creation config
  const enableReferenceImage = creationConfig.showReferenceImage !== false;
  const effectiveHasReferenceImage = enableReferenceImage && (textboxUploadedImages.length > 0);
  const allowedModelIdsByRef = effectiveHasReferenceImage 
    ? MODEL_VISIBILITY_MAPPING.withReferenceImage 
    : MODEL_VISIBILITY_MAPPING.noReferenceImage;
  const allowedModelIds = Array.isArray(creationConfig.allowedModels)
    ? creationConfig.allowedModels
    : allowedModelIdsByRef;

  const hasMask = !!textboxUploadedImages[0]?.maskBlob;

  const finalFilteredModelOptions = filteredModelOptions.filter(m => 
    allowedModelIds.includes(m.id)
  );

  const showOutputSamplesByModel = MODEL_SHOWS_OUTPUT_SAMPLES.includes(selectedModel);
  const showOutputSamples = (creationConfig.showOutputSamples !== false) && showOutputSamplesByModel;

  const computedMessageError = messageError || '';
  const dressSelected = Array.isArray(selectedDresses) && selectedDresses.length > 0;
  const humanModelSelected = selectedHumanModel && (!!selectedHumanModel.gender || !!selectedHumanModel.isImageUpload);
  const productSelected = (productType && productItem);
  const productTaggingComplete = productType && productItem && textboxUploadedImages[0]?.ProductImgDtl?.view;

  // Ensure selectedAIModel is always set to 'google_nano-banana-pro' (hidden from frontend)
  useEffect(() => {
    if (selectedAIModel !== 'google_nano-banana-pro') {
      setSelectedAIModel('google_nano-banana-pro');
    }
  }, [selectedAIModel, setSelectedAIModel]);

  // Load model schema when selectedAIModel changes
  useEffect(() => {
    const loadSchema = async () => {
      if (!selectedAIModel || selectedAIModel === 'auto') {
        setCurrentModelSchema(null);
        return;
      }

      try {
        const schema = await loadModelSchema(selectedAIModel);
        setCurrentModelSchema(schema);
      } catch (error) {
        console.error(`Failed to load schema for model ${selectedAIModel}:`, error);
        setCurrentModelSchema(null);
      }
    };

    loadSchema();
  }, [selectedAIModel]);

  // Calculate cost based on AI model if available, otherwise use base image cost
  const imgCost = React.useMemo(() => {
    // If we have a model schema and it's not 'auto', calculate model cost
    if (currentModelSchema && selectedAIModel && selectedAIModel !== 'auto') {
      // Build model parameters from available props
      const modelParams = {
        resolution: imageQuality || '2K', // Map imageQuality to resolution parameter
      };
      
      // Calculate cost using model schema
      const modelCost = calculateModelCost(currentModelSchema, modelParams, 'image');
      
      // If model cost is available, use it; otherwise fall back to base cost
      if (modelCost > 0) {
        return modelCost;
      }
    }
    
    // Fall back to base image cost calculation
    return calculateImageCost(textboxUploadedImages, realLooks, resolvedCreationSelection?.id);
  }, [currentModelSchema, selectedAIModel, imageQuality, textboxUploadedImages, realLooks, resolvedCreationSelection?.id]);

  // Hide human model selector pill for specific styles
  const HIDE_MODEL_PILL_STYLES = new Set(['Still Life', 'Invisible Mannequin', 'Flat Lay', 'Detail Shot', 'Hanging Shot']);
  const styleId = typeof selectedStyle === 'string' ? selectedStyle : (selectedStyle?.styleId || null);
  const shouldHideHumanModelPill = styleId ? HIDE_MODEL_PILL_STYLES.has(styleId) : false;
  const shouldHideInputsForChangePose = resolvedCreationSelection?.id === 'change_pose';
  const shouldHideInputsForChangeStyle = resolvedCreationSelection?.id === 'change_style';
  const shouldHideInputsForChangeBackground = resolvedCreationSelection?.id === 'change_background';
  const shouldHideInputsForRemoveBackground = resolvedCreationSelection?.id === 'remove_background';

  // Disable Real Looks when mask is present or when text_to_image with Free mode
  const disableRealLooks = hasMask || (resolvedCreationSelection?.id === 'text_to_image' && imageMode === 'free');

  // Default human model for text_to_image
  const DEFAULT_HUMAN_MODEL = {
    gender: "Woman",
    ageGroup: "Adult",
    skinTone: "Fair",
    hairStyle: "Long",
    facialHair: "Clean Shaven",
    bodyType: "Slim",
    ethnicity: "India",
  };

  useEffect(() => {
    if (hasMask && realLooks) {
      setRealLooks(false);
    }
  }, [hasMask, realLooks, setRealLooks]);

  // Set default human model when text_to_image is selected and no model is currently selected
  useEffect(() => {
    if (resolvedCreationSelection?.id === 'text_to_image' && 
        (!selectedHumanModel || (!selectedHumanModel.gender && !selectedHumanModel.isImageUpload))) {
      setSelectedHumanModel(DEFAULT_HUMAN_MODEL);
    }
  }, [resolvedCreationSelection?.id, selectedHumanModel, setSelectedHumanModel]);

  // Send from chatbox
  const handleSendChat = () => {
    handleValidatedGenerateContent();
  };

  // Handle validation before calling parent's handleGenerateContent
  const handleValidatedGenerateContent = () => {

    // Require reference image only for options that truly need it
    if (!(resolvedCreationSelection?.id === 'text_to_image') && textboxUploadedImages.length === 0) {
      toast.error("Please add a Product photo.");
      setIsRefModalOpen(true);
      return;
    }

    // Require product tagging after reference image is uploaded (except for text_to_image and remove_background)
    if (resolvedCreationSelection?.id !== 'text_to_image' && resolvedCreationSelection?.id !== 'remove_background' && textboxUploadedImages.length > 0 && !productTaggingComplete) {
      toast.error("Please add product tags (category, item, view & coverage).");
      setIsTagProductModal1Open(true);
      return;
    }

    // Resolve final message; do NOT fall back to placeholder
    const trimmedInput = (chatInput || '').trim();
    const isPlaceholderLike = trimmedInput === (creationConfig.text_prompt_placeholder || '').trim();

    // For text_to_image, require a non-empty message
    if (resolvedCreationSelection?.id === 'text_to_image' && (!trimmedInput || isPlaceholderLike)) {
      toast.error("Please enter a description to generate an image.");
      try { chatInputRef.current?.focus(); } catch (_) {}
      return;
    }

    if (!trimmedInput || isPlaceholderLike) {
      setMessage('');
    } else {
      setMessage(trimmedInput);
      setChatMessages(prev => [...prev, { role: 'user', content: trimmedInput, ts: Date.now() }]);
    }

    // Proceed with generation after state update tick (do not clear chat input)
    setTimeout(() => {
      handleGenerateContent();
    }, 0);
  };

  useEffect(() => {
    if (!showOutputSamples) {
      setOutputSamples(1);
    }
  }, [showOutputSamples, setOutputSamples]);

  useEffect(() => {
    if (selectedModel && !finalFilteredModelOptions.some(m => m.id === selectedModel)) {
      const fallbackModel = finalFilteredModelOptions[0]?.id || 'free';
      setSelectedModel(fallbackModel);
    }
    // We intentionally depend on instagramPostType, the filtered options length, and the creation option
    // to avoid unnecessary runs while keeping in sync on media type changes and creation config changes.
  }, [instagramPostType, finalFilteredModelOptions.length, selectedCreationOption]);

  // Ensure we always default to the first allowed model for the chosen creation option
  useEffect(() => {
    const firstAllowed = finalFilteredModelOptions[0]?.id;
    if (firstAllowed && selectedModel !== firstAllowed) {
      setSelectedModel(firstAllowed);
    }
  }, [selectedCreationOption, instagramPostType, finalFilteredModelOptions.length]);

  // Ensure aspect ratio stays within allowed set for the selected creation option
  useEffect(() => {
    if (!allowedAspectRatiosSet.has(aspectRatio)) {
      setAspectRatio(allowedAspectRatios[0]);
    }
  }, [selectedCreationOption, allowedAspectRatios.join('|')]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Ensure a default creation option is selected before paint (mandatory field)
  useLayoutEffect(() => {
  }, [selectedCreationOption, setSelectedCreationOption]);

  // Always set Instagram post type; allow creation config to override (defaults to 'story')
  useEffect(() => {
    const desiredPostType = creationConfig.overrideInstagramPostType || 'story';
    setInstagramPostType(desiredPostType);
  }, [setInstagramPostType, selectedCreationOption]);

  return (
    <>
      {/* Global bottom chatbox - Always positioned at bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 px-2 sm:px-4 lg:px-6 pb-2 sm:pb-4 lg:pb-6">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl border border-white/10 bg-[#171717] shadow-2xl backdrop-blur-sm">
            
            {/* Top row with Creation Selector and Model Pill - Inside textarea container */}
            <div className="flex items-center justify-between gap-1.5 px-2 py-1.5 border-b border-white/5">
              {/* Creation Selector - Primary position (shown only when workflow is not complete) */}
              {(contentGenerated || isGeneratingContent) && (
                <div className="relative group flex items-center gap-1.5">
                  {/* Label for clarity */}
                  <span className="text-gray-400 text-[10px] sm:text-xs font-medium whitespace-nowrap">Preset:</span>
                  
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-400/30 px-2.5 py-1.5 hover:from-pink-500/20 hover:to-purple-500/20 hover:border-pink-400/50 transition-all group/btn"
                    onClick={() => setIsCreationModalOpen(true)}
                    data-tour="creation-option"
                    title="Click to change action"
                  >
                    {/* Icon with background */}
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-pink-400/20 to-purple-400/20 text-xs group-hover/btn:scale-110 transition-transform">
                      {(() => {
                        switch(resolvedCreationSelection?.id) {
                          case 'create_image': return '👗';
                          case 'change_pose': return '💃';
                          case 'text_to_image': return '✨';
                          case 'remove_background': return '✂️';
                          case 'change_background': return '🌟';
                          case 'change_style': return '🎨';
                          default: return '✨';
                        }
                      })()}
                    </span>
                    
                    {/* Text label */}
                    <span className="text-pink-200 font-medium text-xs truncate max-w-[120px] sm:max-w-[150px]">
                      {effectiveSelectedLabel}
                    </span>
                    
                    {/* Chevron with subtle animation */}
                    <FaChevronDown className="text-pink-300/70 text-[10px] group-hover/btn:translate-y-0.5 transition-transform" />
                  </button>
                  
                  {/* Enhanced Tooltip on hover */}
                  <div className="absolute hidden group-hover:block bottom-full left-0 mb-2 pointer-events-none z-50">
                    <div className="rounded-xl border border-pink-400/30 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] backdrop-blur-xl px-3 py-2 shadow-2xl shadow-pink-500/20 max-w-[18rem] text-xs leading-relaxed text-white relative">
                      <div className="flex items-center gap-2 mb-1">
                        <FaMagic className="text-pink-400 text-sm" />
                        <div className="font-semibold text-pink-200">{resolvedCreationSelection?.label}</div>
                      </div>
                      <div className="text-gray-300 text-[11px]">{resolvedCreationSelection?.desc}</div>
                      <div className="text-gray-500 text-[10px] mt-1.5 flex items-center gap-1">
                        <FaChevronDown className="text-[8px]" />
                        <span>Click to change</span>
                      </div>
                      {/* Tooltip arrow */}
                      <div className="absolute left-4 -bottom-1 w-2 h-2 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-r border-b border-pink-400/30 rotate-45"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Right-side container: Model pill*/}
              <div className={`flex items-center gap-1.5 ${!isWorkflowComplete ? '' : 'ml-auto'}`}>
                {/* Model Pill - Secondary position */}
                {/* <div
                  className="relative"
                  onMouseEnter={() => {
                    if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
                    setIsModelHoverOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
                    modelHoverTimerRef.current = setTimeout(() => setIsModelHoverOpen(false), 250);
                  }}
                >                  
                  {isModelHoverOpen && (
                    <div
                      className="absolute bottom-full right-0 w-72 bg-gray-900 border border-gray-700/60 rounded-lg shadow-2xl overflow-hidden z-[60] hidden lg:block"
                      onMouseEnter={() => {
                        if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
                        setIsModelHoverOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
                        modelHoverTimerRef.current = setTimeout(() => setIsModelHoverOpen(false), 250);
                      }}
                    >
                      <ul className="max-h-72 overflow-y-auto divide-y divide-gray-800/60">
                        {MODEL_OPTIONS.filter(m => m.type === currentMediaType).map(opt => {
                          const isEnabled = Array.isArray(allowedModelIds) ? allowedModelIds.includes(opt.id) : true;
                          const isSelected = selectedModel === opt.id;
                          return (
                            <li key={opt.id}>
                              <button
                                type="button"
                                className={`w-full flex items-start gap-2 text-left px-2.5 py-1.5 ${isEnabled ? 'hover:bg-gray-800/60' : 'opacity-50 cursor-not-allowed'} ${isSelected ? 'bg-gray-800/40' : ''}`}
                                onClick={() => { if (!isEnabled) return; setSelectedModel(opt.id); setIsModelHoverOpen(false); setIsModelModalOpen(false); }}
                                title={opt.name}
                                aria-disabled={!isEnabled}
                                disabled={!isEnabled}
                              >
                                <span className="flex-1 min-w-0">
                                  <span className="block text-[11px] text-white truncate">{opt.name}</span>
                                  <span className="block text-[10px] text-gray-400 truncate">{opt.desc}</span>
                                  {opt.meta && (
                                    <span className="mt-0.5 flex flex-wrap gap-1 text-[9px] text-gray-300">
                                      {opt.meta.cost != null && <span className="px-1 py-0.5 rounded bg-white/5 border border-white/10">{opt.meta.cost}</span>}
                                      {opt.meta.latencySeconds != null && <span className="px-1 py-0.5 rounded bg-white/5 border border-white/10">{opt.meta.latencySeconds}s</span>}
                                    </span>
                                  )}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div> */}

              {/* AI Model Selector - Hidden from frontend, always set to 'google_nano-banana-pro' */}
              {/* <div className="flex-shrink-0 min-w-0 max-w-full sm:max-w-none"> 
                <AIModelSelector
                  selectedModelId={selectedAIModel || 'auto'}
                  onModelSelect={setSelectedAIModel}
                  allowedModelIds={['google_nano-banana-pro']}
                />
              </div> */}



              </div>
            </div>

            {/* Error message */}
            {creationOptionError && (
              <div className="px-2 py-1 text-[10px] text-red-400 flex items-center gap-1 border-b border-white/5">
                <span>⚠️</span>
                {creationOptionError}
              </div>
            )}

            {/* Main textarea and generate button */}
            <div className="p-1.5 sm:p-2">
              <div className="flex items-center gap-1 sm:gap-1.5">
                <textarea
                  ref={(el) => { chatInputRef.current = el; if (messageTextareaRef) { messageTextareaRef.current = el; } }}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={creationConfig.text_prompt_placeholder}
                  className="w-full bg-transparent border-0 rounded-lg px-2.5 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-0 resize-none placeholder:text-gray-500 placeholder:italic placeholder:opacity-70"
                  style={{ minHeight: '16px', maxHeight: '80px' }}
                  readOnly={shouldHideInputsForRemoveBackground}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChat();
                    }
                  }}
                  onFocus={(e) => {
                    const current = (chatInput || '').trim();
                    const ph = (creationConfig.text_prompt_placeholder || '').trim();
                    if (current && current === ph) {
                      try { e.target.select(); } catch (_) {}
                      setChatInput('');
                    }
                  }}
                />
                {/* Reference image thumbnail */}
                {isReferenceLoading ? (
                  <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-white/10 bg-gray-800 flex items-center justify-center">
                    <span className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  resolvedCreationSelection?.id !== 'text_to_image' && textboxUploadedImages.length > 0 && (
                    <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-white/10">
                      <img
                        src={textboxUploadedImages[0].preview || textboxUploadedImages[0].url}
                        alt="Reference"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )
                )}
                <button
                  type="button"
                  disabled={isGeneratingContent}
                  onClick={handleSendChat}
                  data-tour="generate-button"
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-xs font-semibold transition-all duration-300 shadow-lg ${
                    isGeneratingContent 
                      ? 'bg-gray-600 text-white/80 cursor-not-allowed animate-pulse' 
                      : isWorkflowComplete 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-green-500/30' 
                        : 'bg-white text-black hover:opacity-90'
                  }`}
                  title={isWorkflowComplete ? "All steps completed! Ready to generate" : "Generate"}
                >
                  {isGeneratingContent ? (
                    <FaSpinner className="text-xs animate-spin" />
                  ) : (
                    <FaMagic className={`text-xs ${isWorkflowComplete ? 'animate-bounce' : ''}`} />
                  )}
                  <span className="flex flex-col items-start">
                    <span>
                      {isGeneratingContent ? 'Generating...' : 'Generate'}
                    </span>
                    {!isGeneratingContent && imgCost > 0 && (
                      <span className="text-[9px] font-normal opacity-75 leading-tight">
                        ₹{imgCost.toFixed(2)} incl. GST
                      </span>
                    )}
                  </span>
                </button>
              </div>

              {/* Settings pills row */}
              <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mt-1.5">
                {/* Reference image pill (hidden for text_to_image) */}
                {creationConfig.showReferenceImage !== false && resolvedCreationSelection?.id !== 'text_to_image' && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsRefModalOpen(true)}
                      data-tour="upload-section"
                      title="Reference image"
                      aria-describedby="ref-tooltip"
                      disabled={isReferenceLoading}
                    >
                      {isReferenceLoading ? (
                        <span className="w-3.5 h-3.5 border-2 border-gray-300/70 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <FaPaperclip className="text-blue-300/90 text-[20px]" />
                          {textboxUploadedImages.length > 0 && (
                            <span className="ml-0.5 text-gray-400 text-[20px]">✓</span>
                          )}
                        </>
                      )}
                    </button>
                    <div
                      id="ref-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Upload photo of the Apparel.</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}


                {/* Tag Product1 pill - Detailed product tagging (shown for all creation options except text_to_image) */}
                {resolvedCreationSelection?.id !== 'text_to_image' && !shouldHideInputsForRemoveBackground && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsTagProductModal1Open(true)}
                      title="Detailed product tagging"
                      aria-describedby="tag-product1-tooltip"
                    >
                      <FaTshirt className="text-purple-300/90 text-[20px]" />
                      {productTaggingComplete && (
                        <span className="ml-0.5 text-gray-400 text-[20px]">✓</span>
                      )}
                    </button>
                    <div
                      id="tag-product1-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Tag category, item, view & coverage</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Template pill */}
                {/* {!shouldHideInputsForChangePose && !shouldHideInputsForChangeStyle && !shouldHideInputsForRemoveBackground && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsTemplateModalOpen(true)}
                      title="Template"
                      aria-describedby="template-tooltip"
                    >
                      <FaImage className="text-yellow-300/90 text-[20px]" />
                      <span className="truncate max-w-[60px]">
                        {selectedTemplate || 'Template'}
                      </span>
                    </button>
                    <div
                      id="template-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Choose a template for AI image</div>
                        <div className="text-gray-300">
                          {selectedTemplate ? `Selected: ${selectedTemplate}` : 'Pick one'}
                        </div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )} */}
                
                {/* Style pill */}
                {!shouldHideInputsForChangePose && !shouldHideInputsForChangeBackground && !shouldHideInputsForRemoveBackground && (
                <div
                  className="relative group"
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                    onClick={() => setIsStyleModalOpen(true)}
                    data-tour="style-selector"
                    title="Style"
                    aria-describedby="style-tooltip"
                  >
                    {selectedStyle?.isImageUpload && selectedStyle?.imagePreview ? (
                      <>
                        <img 
                          src={selectedStyle.imagePreview} 
                          alt="Style" 
                          className="w-5 h-5 rounded-full object-cover border border-white/20"
                        />
                        <span className="truncate max-w-[60px]">Style</span>
                      </>
                    ) : (
                      <>
                        <FaPalette className="text-cyan-300/90 text-[20px]" />
                        <span className="truncate max-w-[60px]">
                          {typeof selectedStyle === 'string' 
                            ? selectedStyle 
                            : 'Style'}
                        </span>
                      </>
                    )}
                  </button>
                  <div
                    id="style-tooltip"
                    role="tooltip"
                    className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                  >
                    <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                      <div>Choose a style of Image</div>
                      <div className="text-gray-300">
                        {selectedStyle?.isImageUpload 
                          ? 'Image uploaded' 
                          : typeof selectedStyle === 'string' 
                            ? `Selected: ${selectedStyle}` 
                            : 'Pick one'}
                      </div>
                      <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                    </div>
                  </div>
                </div>
                )}

                {/* Event selector pill */}
                {!shouldHideInputsForChangePose && !shouldHideInputsForRemoveBackground && (
                <div className="relative group">
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                    onClick={() => setIsEventModalOpen(true)}
                    data-tour="event-selector"
                    title="Event"
                    aria-describedby="event-tooltip"
                  >
                    <FaCalendarAlt className="text-green-300/90 text-[20px]" />
                    <span className="truncate max-w-[40px]">{selectedEvent || 'Event'}</span>
                  </button>
                  <div
                    id="event-tooltip"
                    role="tooltip"
                    className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                  >
                    <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                      <div>Choose an event</div>
                      <div className="text-gray-300">{selectedEvent ? `Selected: ${selectedEvent}` : 'Optional'}</div>
                      <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                                    </div>
                </div>
                </div>
                )}

                {/* Human model selector pill */}
                {((!shouldHideInputsForChangePose && !shouldHideInputsForChangeBackground && !shouldHideInputsForRemoveBackground && !shouldHideInputsForChangeStyle) && !shouldHideHumanModelPill) && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsHumanModelModalOpen(true)}
                      data-tour="model-selector"
                      title="Model"
                      aria-describedby="model-tooltip"
                    >
                      {selectedHumanModel?.isImageUpload && selectedHumanModel?.imagePreview ? (
                        <>
                          <img 
                            src={selectedHumanModel.imagePreview} 
                            alt="Model" 
                            className="w-5 h-5 rounded-full object-cover border border-white/20"
                          />
                          <span className="truncate max-w-[35px]">Model</span>
                        </>
                      ) : (
                        <>
                          <FaUser className="text-blue-300/90 text-[20px]" />
                          <span className="truncate max-w-[35px]">{selectedHumanModel?.gender || 'Model'}</span>
                        </>
                      )}
                    </button>
                    <div
                      id="model-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Select the human model for your apparel</div>
                        <div className="text-gray-300">
                          {selectedHumanModel?.isImageUpload 
                            ? 'Image uploaded' 
                            : selectedHumanModel?.gender 
                              ? `Selected: ${selectedHumanModel.gender}` 
                              : 'Choose one'}
                        </div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Aspect Ratio pill */}
                {!shouldHideInputsForChangeStyle && !shouldHideInputsForChangePose && !shouldHideInputsForRemoveBackground && !shouldHideInputsForChangeBackground && (<div className="relative group">
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                    onClick={() => setIsAspectModalOpen(true)}
                    data-tour="aspect-ratio"
                    title="Aspect ratio"
                    aria-describedby="aspect-tooltip"
                  >
                    <AspectIcon ratio={aspectRatio} className="w-6 h-6" />
                    <span>{aspectRatio}</span>
                  </button>
                  <div
                    id="aspect-tooltip"
                    role="tooltip"
                    className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                  >
                    <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                      <div>Set aspect ratio</div>
                      <div className="text-gray-300">Current: {aspectRatio}</div>
                      <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                    </div>
                  </div>
                </div>)}

                {/* Image Quality pill - only when text_to_image and premium mode */}
                {/* {resolvedCreationSelection?.id === 'text_to_image' && imageMode === 'premium' && ( */}
                {!shouldHideInputsForRemoveBackground && (
                <div className="relative group">
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                    onClick={() => setIsImageQualityModalOpen(true)}
                    title="Image Quality"
                  >
                    <span className="text-[12px] font-medium">{imageQuality || '2K'}</span>
                  </button>
                </div>
                )}

                {/* Output Samples pill */}
                {showOutputSamples && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsSamplesModalOpen(true)}
                      title="Output samples"
                      aria-describedby="samples-tooltip"
                    >
                      <span>{outputSamples}×</span>
                    </button>
                    <div
                      id="samples-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Number of outputs</div>
                        <div className="text-gray-300">{outputSamples ? `Current: ${outputSamples}` : ''}</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
                </div>
      </div>

    {/* Creation options modal */}
    {isCreationModalOpen && (
      <CreationOptionsModal
        isOpen={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
        options={CREATION_OPTIONS}
        selectedLabel={effectiveSelectedLabel}
        onSelect={(labelOrId) => { const opt = CREATION_OPTIONS.find(o => o.label === labelOrId) || CREATION_OPTIONS.find(o => o.id === labelOrId); if (opt) { setSelectedCreationOption(opt.id); } setIsCreationModalOpen(false); }}
      />
    )}
    
    {/* Setting modals mounted */}
    {isRefModalOpen && (
      <ReferenceImageModal
        isOpen={isRefModalOpen}
        onClose={() => setIsRefModalOpen(false)}
        textboxUploadedImages={textboxUploadedImages}
        textboxUploadError={textboxUploadError}
        handleTextboxFileUpload={handleTextboxFileUpload}
        removeTextboxImage={removeTextboxImage}
        textboxFileInputRef={textboxFileInputRef}
        setTextboxUploadedImages={setTextboxUploadedImages}
        disableMasking={shouldHideInputsForRemoveBackground}
        instagramPostType={instagramPostType}
      />
    )}
    {/* {isTagProductModalOpen && (
      <TagProductModal
        isOpen={isTagProductModalOpen}
        onClose={() => setIsTagProductModalOpen(false)}
        productType={productType}
        setProductType={setProductType}
        productItem={productItem}
        setProductItem={setProductItem}
        setSelectedDresses={setSelectedDresses}
        instagramPostType={instagramPostType}
      />
    )} */}
    {isTagProductModal1Open && (
      <TagProductModal1
        isOpen={isTagProductModal1Open}
        onClose={() => setIsTagProductModal1Open(false)}
        productType={productType}
        setProductType={setProductType}
        productItem={productItem}
        setProductItem={setProductItem}
        textboxUploadedImages={textboxUploadedImages}
        setTextboxUploadedImages={setTextboxUploadedImages}
      />
    )}
    {isTemplateModalOpen && (
      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
      />
    )}
    {isAspectModalOpen && (
      <AspectRatioModal
        isOpen={isAspectModalOpen}
        onClose={() => setIsAspectModalOpen(false)}
        aspectRatio={aspectRatio}
        setAspectRatio={setAspectRatio}
        allowedAspectRatiosSet={allowedAspectRatiosSet}
        AspectIcon={AspectIcon}
      />
    )}
    {isSamplesModalOpen && (
      <OutputSamplesModal
        isOpen={isSamplesModalOpen}
        onClose={() => setIsSamplesModalOpen(false)}
        outputSamples={outputSamples}
        setOutputSamples={setOutputSamples}
        isFreeModel={isFreeModel}
      />
    )}
    {isStyleModalOpen && (
      <StyleModal
        isOpen={isStyleModalOpen}
        onClose={() => setIsStyleModalOpen(false)}
        selectedStyle={selectedStyle}
        setSelectedStyle={setSelectedStyle}
      />
    )}
    {isModelModalOpen && (
      <ModelModal
        isOpen={isModelModalOpen}
        onClose={() => setIsModelModalOpen(false)}
        options={MODEL_OPTIONS.filter(m => m.type === currentMediaType)}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        enabledIds={finalFilteredModelOptions.map(m => m.id)}
      />
    )}
    {isImageModeModalOpen && (
      <ImageModelModal
        isOpen={isImageModeModalOpen}
        onClose={() => setIsImageModeModalOpen(false)}
        mode={imageMode}
        setMode={setImageMode}
      />
    )}
    {isImageQualityModalOpen && (
      <ImageQualityModal
        isOpen={isImageQualityModalOpen}
        onClose={() => setIsImageQualityModalOpen(false)}
        imageQuality={imageQuality}
        setImageQuality={setImageQuality}
        isFreeModel={false}
      />
    )}
    {isHumanModelModalOpen && (
      <HumanModelModal
        isOpen={isHumanModelModalOpen}
        onClose={() => setIsHumanModelModalOpen(false)}
        selectedHumanModel={selectedHumanModel}
        setSelectedHumanModel={setSelectedHumanModel}
      />
    )}

    {isEventModalOpen && (
      <EventSelectorModal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
      />
    )}

    {/* ProductSelectorModal removed: selection now handled inside ReferenceImageModal */}

            {/* Cost Help Modal */}
            {/* <CostHelpModal isOpen={isCostHelpOpen} onClose={() => setIsCostHelpOpen(false)} /> */}
    </>
  );
};

export default CreateImageEditor; 